import { ApiError, createIdempotencyKey, type IdempotencyKey, type Problem, type Versioned } from "@baton/api";
import type { QueryClient, QueryKey } from "@tanstack/query-core";
import type { EntityQueue } from "./serialise.ts";

/**
 * FR-015-021, `contracts/client-api.md` § Optimistic updates, `research.md` W-07: snapshot
 * with `base_version`, apply with pending field marks, send a merge patch of the changed
 * fields only with `If-Match` and the idempotency key, write the response verbatim on
 * success, restore the snapshot on any error other than 412, and hand a 412 to the
 * reconciliation of `reconcile.ts`, which never rolls back.
 */

/** § Which writes are optimistic. */
export const OPTIMISTIC_WRITES = {
  "item.field": true,
  "item.title": true,
  "item.description": true,
  "comment.post": true,
  "comment.edit": true,
  "comment.delete": true,
  "checklist.change": true,
  "watch.toggle": true,
  "relation.change": true,
  "reorder": true,
  "bulk": false,
  "create": false,
  "destructive": false,
  "settings": false,
} as const;

export type WriteKind = keyof typeof OPTIMISTIC_WRITES;

export function isOptimistic(kind: WriteKind): boolean {
  return OPTIMISTIC_WRITES[kind];
}

/** Title and description edits are debounced 800 ms and flushed on blur (§ Which writes are optimistic). */
export const TEXT_DEBOUNCE_MS = 800;

export interface PendingMarks {
  /** The fields of an entity with an in-flight optimistic write, for the 70 percent tint. */
  fieldsOf(entityId: string): ReadonlySet<string>;
  subscribe(listener: () => void): () => void;
}

export interface PendingStore extends PendingMarks {
  mark(entityId: string, fields: readonly string[]): void;
  clear(entityId: string, fields: readonly string[]): void;
}

export function createPendingStore(): PendingStore {
  const marks = new Map<string, Set<string>>();
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  return {
    fieldsOf: (entityId) => marks.get(entityId) ?? new Set(),
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    mark(entityId, fields) {
      const set = marks.get(entityId) ?? new Set<string>();
      for (const field of fields) set.add(field);
      marks.set(entityId, set);
      notify();
    },
    clear(entityId, fields) {
      const set = marks.get(entityId);
      if (!set) return;
      for (const field of fields) set.delete(field);
      if (set.size === 0) marks.delete(entityId);
      notify();
    },
  };
}

export interface Snapshot {
  readonly entries: readonly { readonly key: QueryKey; readonly data: unknown }[];
  readonly baseVersion: number;
}

export interface OptimisticWrite<E extends Versioned> {
  readonly queryClient: QueryClient;
  readonly pending: PendingStore;
  readonly queue: EntityQueue;
  /** The entity's one key, which every list reads through. */
  readonly entityKey: QueryKey;
  readonly entity: E;
  /** The fields to change and their new values; only fields whose value differs are sent. */
  readonly patch: Readonly<Partial<E>>;
  /** Other cache entries whose membership or ordering the change could move (list keys). */
  readonly touchedKeys?: readonly QueryKey[];
  /** `PATCH` with the merge patch, the version for `If-Match` and the key minted at creation. */
  readonly send: (patch: Readonly<Partial<E>>, options: { version: number; idempotencyKey: IdempotencyKey }) => Promise<E>;
  /** The key minted when the mutation was created; minted here when the caller has none yet. */
  readonly idempotencyKey?: IdempotencyKey;
  /** A 412: refetch, present the conflict, never roll back (`reconcile.ts`). */
  readonly onConflict: (conflict: { readonly pending: Readonly<Partial<E>>; readonly baseVersion: number; readonly resend: (version: number) => Promise<E> }) => void;
  /** Any other error: the snapshot is restored and this raises the toast naming item, field and problem. */
  readonly onError: (problem: Problem, fields: readonly string[]) => void;
}

export function changedFields<E extends object>(entity: E, patch: Readonly<Partial<E>>): (keyof E & string)[] {
  return (Object.keys(patch) as (keyof E & string)[]).filter((field) => !deepEqual(entity[field], patch[field]));
}

export function mergePatch<E extends object>(patch: Readonly<Partial<E>>, fields: readonly (keyof E & string)[]): Partial<E> {
  const out: Partial<E> = {};
  for (const field of fields) out[field] = patch[field];
  return out;
}

export function takeSnapshot(queryClient: QueryClient, keys: readonly QueryKey[], baseVersion: number): Snapshot {
  return { entries: keys.map((key) => ({ key, data: queryClient.getQueryData(key) })), baseVersion };
}

export function restoreSnapshot(queryClient: QueryClient, snapshot: Snapshot): void {
  for (const { key, data } of snapshot.entries) queryClient.setQueryData(key, data);
}

/** Run one optimistic write through the whole sequence. Resolves with the server's entity. */
export async function optimisticWrite<E extends Versioned>(write: OptimisticWrite<E>): Promise<E | null> {
  const { queryClient, pending, queue, entityKey, entity, patch, send } = write;
  const fields = changedFields(entity, patch);
  if (fields.length === 0) return entity;
  const merge = mergePatch(patch, fields);
  const idempotencyKey = write.idempotencyKey ?? createIdempotencyKey();
  const snapshot = takeSnapshot(queryClient, [entityKey, ...(write.touchedKeys ?? [])], entity.version);
  queryClient.setQueryData<E>(entityKey, (current) => ({ ...(current ?? entity), ...merge }));
  pending.mark(entity.id, fields);
  try {
    const response = await queue.run(entity.id, entity.version, (version) => send(merge, { version, idempotencyKey }));
    queryClient.setQueryData<E>(entityKey, response);
    return response;
  } catch (error) {
    if (error instanceof ApiError && error.status === 412) {
      // A 412 is a completed answer the server stores under the key (001 § Idempotency), so a
      // resend under the same key would replay the 412; the confirmed retry is a new mutation.
      write.onConflict({ pending: merge, baseVersion: snapshot.baseVersion, resend: (version) => send(merge, { version, idempotencyKey: createIdempotencyKey() }) });
      return null;
    }
    restoreSnapshot(queryClient, snapshot);
    const problem: Problem = error instanceof ApiError ? error.problem : { type: "transport", title: error instanceof Error ? error.message : "Request failed", status: 0, request_id: "" };
    write.onError(problem, fields);
    throw error;
  } finally {
    pending.clear(entity.id, fields);
  }
}

/** Structural equality of two read-shape values, for the changed fields of a write and of a conflict. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}
